import {Component, Inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {GraphMap} from '../api/interfaces/graph-node.interface';
import {OpenaiService} from '../api/services/openai.service';
import {JobService} from '../api/services/job.service';
import {NotificationService} from '../services/notification.service';
import {GenerationStateService} from '../services/generation-state.service';
import {ProposalPreviewDialogComponent} from './proposal-preview-dialog.component';
import {Job} from '../api/interfaces/jobs.dto';
import {
  MaporiumAccordionItemComponent
} from '../shared/components/maporium-accordion/maporium-accordion-item.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

export interface AiOptimizerDialogData {
  map: GraphMap;
  canEdit: boolean;
}

type StepStatus = 'idle' | 'running' | 'done' | 'error';

interface OptimizerStep {
  key: 'analyze' | 'optimize' | 'proposal';
  icon: string;
  title: string;
  description: string;
  status: StepStatus;
  detail?: string;
}

@Component({
  selector: 'app-ai-optimizer-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MaporiumAccordionItemComponent,
    TranslatePipe,
  ],
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="material-symbols-rounded me-2">troubleshoot</span>
        {{ 'AI_OPTIMIZER.TITLE' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" (click)="close()"
            [matTooltip]="'EMBED_DIALOG.TOOLTIP_CLOSE_PANEL' | translate"></span>
    </div>

    <div mat-dialog-content>
      <maporium-accordion-item [title]="'AI_OPTIMIZER.PIPELINE' | translate">
        @for (step of steps; track step.key) {
          <div class="account-row">
            <div class="row-icon"
                 [class.status-done]="step.status === 'done'"
                 [class.status-error]="step.status === 'error'">
              <span class="material-symbols-rounded">
                {{ step.status === 'done' ? 'check_circle' : step.status === 'error' ? 'error' : step.icon }}
              </span>
            </div>
            <div class="row-info">
              <div class="step-title">{{ step.title }}</div>
              <div class="step-hint">{{ step.detail || step.description }}</div>
            </div>
            <div class="row-actions">
              @if (step.status === 'running') {
                <mat-spinner diameter="20"></mat-spinner>
              } @else {
                @if (step.key === 'proposal' && latestSlug) {
                  <button class="dialog-action-btn dialog-action-btn-cancel"
                          (click)="viewProposal(latestSlug)">{{ 'AI_OPTIMIZER.VIEW' | translate }}</button>
                }
                <button class="dialog-action-btn dialog-action-btn-cancel"
                        [disabled]="!data.canEdit || pipelineRunning"
                        (click)="run(step.key)">
                  {{ (step.status === 'done' ? 'AI_OPTIMIZER.RERUN' : 'AI_OPTIMIZER.RUN') | translate }}
                </button>
              }
            </div>
          </div>
        }

        @if (optimizedMapId && optimizedMapId !== data.map.id) {
          <div class="account-row">
            <div class="row-icon"><span class="material-symbols-rounded">auto_fix_high</span></div>
            <div class="row-info">{{ 'AI_OPTIMIZER.OPTIMIZED_READY' | translate }}</div>
            <div class="row-actions">
              <button class="dialog-action-btn dialog-action-btn-cancel" (click)="openOptimizedMap()">
                {{ 'AI_OPTIMIZER.OPEN_SMAP' | translate }}
              </button>
            </div>
          </div>
        }
        @if (analyzeNeedsReload) {
          <div class="account-row">
            <div class="row-icon"><span class="material-symbols-rounded">warning_amber</span></div>
            <div class="row-info">{{ 'AI_OPTIMIZER.RELOAD_FOR_FLAGS' | translate }}</div>
            <div class="row-actions">
              <button class="dialog-action-btn dialog-action-btn-cancel" (click)="reload()">{{ 'AI_OPTIMIZER.RELOAD' | translate }}</button>
            </div>
          </div>
        }
      </maporium-accordion-item>
    </div>

    <div mat-dialog-actions class="d-flex justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" (click)="close()">{{ 'AI_OPTIMIZER.CLOSE' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary"
              [disabled]="!data.canEdit || anyRunning"
              (click)="runAll()">{{ 'AI_OPTIMIZER.RUN_FULL_PIPELINE' | translate }}</button>
    </div>
  `,
  styles: [`
    .account-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 6px 8px;
      padding: 6px 0;
    }

    .row-icon {
      width: 30px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      color: #555;

      .material-symbols-rounded {
        font-size: 22px;
      }

      &.status-done {
        color: #2e7d32;
      }

      &.status-error {
        color: #cc0000;
      }
    }

    .row-info {
      flex: 1;
      flex-shrink: 1;
      min-width: 0;
    }

    .step-title {
      font-size: 14px;
      color: #333;
    }

    .step-hint {
      font-size: 12px;
      color: #666;
    }

    .row-actions {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 6px;
      margin-left: auto;
    }

    .dialog-action-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `],
})
export class AiOptimizerDialogComponent implements OnInit {
  steps: OptimizerStep[] = [];

  optimizedMapId?: string;
  latestSlug?: string;
  analyzeNeedsReload = false;
  pipelineRunning = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AiOptimizerDialogData,
    private dialogRef: MatDialogRef<AiOptimizerDialogComponent>,
    private dialog: MatDialog,
    private router: Router,
    private openaiService: OpenaiService,
    private jobService: JobService,
    private notificationService: NotificationService,
    private generationStateService: GenerationStateService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.steps = [
      {
        key: 'analyze',
        icon: 'troubleshoot',
        title: this.translate.instant('AI_OPTIMIZER.STEP_ANALYZE_TITLE'),
        description: this.translate.instant('AI_OPTIMIZER.STEP_ANALYZE_DESC'),
        status: 'idle',
      },
      {
        key: 'optimize',
        icon: 'auto_fix_high',
        title: this.translate.instant('AI_OPTIMIZER.STEP_OPTIMIZE_TITLE'),
        description: this.translate.instant('AI_OPTIMIZER.STEP_OPTIMIZE_DESC'),
        status: 'idle',
      },
      {
        key: 'proposal',
        icon: 'web',
        title: this.translate.instant('AI_OPTIMIZER.STEP_PROPOSAL_TITLE'),
        description: this.translate.instant('AI_OPTIMIZER.STEP_PROPOSAL_DESC'),
        status: 'idle',
      },
    ];

    // If this map is itself an AI-optimized smap, the proposal step targets it directly
    const sourceMeta = this.data.map.metadata?.find((m) => m.key === 'source.map');
    if (sourceMeta) {
      this.optimizedMapId = this.data.map.id;
      this.step('optimize').status = 'done';
      this.step('optimize').detail = this.translate.instant('AI_OPTIMIZER.ALREADY_OPTIMIZED');
    }
    this.openaiService.getProposalsForMap(this.data.map.id).subscribe({
      next: (proposals) => {
        if (proposals?.length) {
          this.latestSlug = proposals[0].publicSlug;
          this.step('proposal').status = 'done';
          this.step('proposal').detail = this.translate.instant('AI_OPTIMIZER.PROPOSAL_EXISTS');
        }
      },
      error: () => undefined,
    });
  }

  get anyRunning(): boolean {
    return this.pipelineRunning || this.steps.some((s) => s.status === 'running');
  }

  close(): void {
    this.dialogRef.close();
  }

  run(key: OptimizerStep['key']): void {
    switch (key) {
      case 'analyze':
        void this.runAnalyze();
        break;
      case 'optimize':
        void this.runOptimize();
        break;
      case 'proposal':
        void this.runProposal();
        break;
    }
  }

  async runAll(): Promise<void> {
    this.pipelineRunning = true;
    try {
      await this.runAnalyze();
      await this.runOptimize();
      await this.runProposal();
    } catch {
      // The failing step already surfaced its error
    } finally {
      this.pipelineRunning = false;
    }
  }

  viewProposal(slug: string): void {
    this.dialog.open(ProposalPreviewDialogComponent, {width: '900px', data: {slug}});
  }

  openOptimizedMap(): void {
    if (!this.optimizedMapId) return;
    this.dialogRef.close();
    void this.router.navigate(['/dashboard/map', this.optimizedMapId]);
  }

  reload(): void {
    window.location.reload();
  }

  private runAnalyze(): Promise<void> {
    return this.runStep('analyze', this.openaiService.analyzeBottlenecks(this.data.map.id), (job) => {
      const found = job.result?.rawData?.bottlenecksFound ?? 0;
      this.step('analyze').detail = this.translate.instant(
        found === 1 ? 'AI_OPTIMIZER.FLAGGED_SINGULAR' : 'AI_OPTIMIZER.FLAGGED_PLURAL',
        {count: found},
      );
      this.analyzeNeedsReload = found > 0;
    });
  }

  private runOptimize(): Promise<void> {
    return this.runStep('optimize', this.openaiService.optimizeMap(this.data.map.id), (job) => {
      if (job.result?.entityId) {
        this.optimizedMapId = job.result.entityId;
      }
      this.step('optimize').detail = this.translate.instant('AI_OPTIMIZER.OPTIMIZED_GENERATED');
    });
  }

  private runProposal(): Promise<void> {
    // Prefer the optimized smap generated in this session; the backend also
    // resolves the optimized counterpart when handed the source map id.
    const targetMapId = this.optimizedMapId || this.data.map.id;
    return this.runStep('proposal', this.openaiService.generateProposal(targetMapId), (job) => {
      const slug = job.result?.rawData?.publicSlug;
      if (slug) {
        this.latestSlug = slug;
        this.step('proposal').detail = this.translate.instant('AI_OPTIMIZER.PROPOSAL_READY');
        this.viewProposal(slug);
      }
    });
  }

  private runStep(
    key: OptimizerStep['key'],
    start: ReturnType<OpenaiService['analyzeBottlenecks']>,
    onComplete: (job: Job) => void,
  ): Promise<void> {
    const step = this.step(key);
    step.status = 'running';
    step.detail = undefined;
    return new Promise<void>((resolve, reject) => {
      start.subscribe({
        next: (job) => {
          this.generationStateService.startSmapGeneration(job.id);
          this.jobService.trackJob(job.id, (updatedJob) => {
            if (updatedJob.status === 'COMPLETED') {
              this.generationStateService.endSmapGeneration(updatedJob.id);
              step.status = 'done';
              onComplete(updatedJob);
              resolve();
            } else if (updatedJob.status === 'FAILED') {
              this.generationStateService.endSmapGeneration(updatedJob.id);
              step.status = 'error';
              const message: string = updatedJob.error || this.translate.instant('AI_OPTIMIZER.STEP_FAILED', {title: step.title});
              step.detail = message;
              this.notificationService.error(message);
              reject(new Error(message));
            }
          });
        },
        error: (err) => {
          step.status = 'error';
          const message: string = err?.error?.message || err?.message || this.translate.instant('AI_OPTIMIZER.STEP_FAILED_TO_START', {title: step.title});
          step.detail = message;
          this.notificationService.error(message);
          reject(err);
        },
      });
    });
  }

  private step(key: OptimizerStep['key']): OptimizerStep {
    return this.steps.find((s) => s.key === key) as OptimizerStep;
  }
}
